import { useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from './ui/utils';
import { HeaderSearchBar } from '@/components/HeaderSearchBar';
import { UserProfileMenu } from '@/components/UserProfileMenu';
import beatlyLogo from 'figma:asset/78166de18001e30ac00ef2150ee31fcaaf218dc5.png';

interface TopNavBarProps {
  currentPage?: string;
  onPageChange?: (page: string) => void;
  sidebarCollapsed?: boolean;
  userName?: string;
  brandName?: string;
  activeMode?: 'chat' | 'cowork' | 'platform';
  onModeChange?: (mode: 'chat' | 'cowork' | 'platform') => void;
  /** When false, hide Platform/Cowork/Chat mode switch (staging-only feature). */
  showCowork?: boolean;
}

/**
 * When false, the Platform / Cowork / Chat pills are hidden (product default).
 * Ways to show the switcher again for internal dev:
 * - Open any URL with ?showModes=1 (also persists for the browser tab session)
 * - In DevTools: localStorage.setItem('beatly_show_mode_switch','1') then reload
 * - Set VITE_SHOW_MODE_SWITCH=true in .env (local builds)
 * Navigating to /chat or /cowork still works without the switcher; use / for Platform.
 * When {@link TopNavBarProps.showCowork} is true (API/team), the switcher is shown regardless of this.
 */
function isModeSwitcherVisible(searchParams: URLSearchParams): boolean {
  if (import.meta.env.VITE_SHOW_MODE_SWITCH === 'true') return true;
  try {
    if (localStorage.getItem('beatly_show_mode_switch') === '1') return true;
    if (sessionStorage.getItem('beatly_show_mode_switch') === '1') return true;
  } catch {
    /* private mode or no storage */
  }
  return searchParams.get('showModes') === '1';
}

/** Temporarily hide AI Assistant + Notifications; set to false to show again. */
const HIDE_TOP_NAV_AI_AND_NOTIFICATIONS = true;

export function TopNavBar({
  currentPage: _currentPage = '',
  onPageChange,
  activeMode = 'platform',
  onModeChange,
  showCowork = false,
}: TopNavBarProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const showModeSwitcher = useMemo(() => isModeSwitcherVisible(searchParams), [searchParams]);
  const showCenterModeSwitch = showCowork || showModeSwitcher;

  useEffect(() => {
    if (searchParams.get('showModes') === '1') {
      try {
        sessionStorage.setItem('beatly_show_mode_switch', '1');
      } catch {
        /* ignore */
      }
    }
  }, [searchParams]);
  const navRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={navRef}
      className="h-14 bg-white flex items-center pr-5 shrink-0 z-50 relative"
    >
      {/* Left: Logo – generous padding, natural sizing */}
      <div className="flex items-center shrink-0 pl-5 pr-4">
        <img src={beatlyLogo} alt={t('nav.logoAlt')} className="h-8 w-auto" />
      </div>

      {/* Center: Mode Switch (team show_cowork and/or dev isModeSwitcherVisible) */}
      {showCenterModeSwitch ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center bg-[#f4f4f5] rounded-full p-[3px]">
            {(['platform', 'cowork', 'chat'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => onModeChange?.(mode)}
                className={cn(
                  'relative px-4 py-1.5 rounded-full text-[13px] transition-all duration-200 capitalize',
                  activeMode === mode
                    ? 'bg-white text-[#101828] shadow-[0_1px_3px_rgba(0,0,0,0.08)]'
                    : 'text-[#667085] hover:text-[#344054]'
                )}
                style={{ fontWeight: activeMode === mode ? 600 : 500 }}
                aria-pressed={activeMode === mode}
              >
                {mode === 'chat' ? t('nav.modeChat') : mode === 'cowork' ? t('nav.modeCowork') : t('nav.modePlatform')}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      {/* Right: Search | AI | Notifications | Profile | Settings */}
      <div className="flex items-center gap-1.5 shrink-0">
        <HeaderSearchBar />

        {!HIDE_TOP_NAV_AI_AND_NOTIFICATIONS ? (
          <>
            {/* AI */}
            <button
              type="button"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-[#be43ff] hover:bg-[#be43ff]/8 transition-colors"
              title={t('nav.aiAssistant')}
              aria-label={t('nav.aiAssistant')}
            >
              <Sparkles className="w-[18px] h-[18px]" />
            </button>

            {/* Notifications */}
            <button
              type="button"
              className="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              title={t('nav.notifications')}
              aria-label={t('nav.notifications')}
            >
              <Bell className="w-[18px] h-[18px]" />
              <span className="absolute top-1 right-1.5 w-2 h-2 bg-[#a9f600] rounded-full border-2 border-white" />
            </button>
          </>
        ) : null}

        {/* Divider */}
        <div className="w-px h-6 bg-gray-200 mx-1.5" />

        <UserProfileMenu onPageChange={onPageChange} />
      </div>
    </div>
  );
}
